import { memo } from "react";
import { Box, Divider, ListSubheader, MenuItem, Select, Typography } from "@mui/material";
import {
  ALLOCATE_BUDGET_TYPE_OPTIONS,
  BUDGET_FAMILY,
  SET_BUDGET_TYPE_OPTIONS,
  getBudgetTypeLabel,
} from "../lib/constants";
import { ABO_COLORS, tableControlSx } from "../lib/campaignAboStyles";

const groupHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  lineHeight: "28px",
  px: 1.5,
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  color: ABO_COLORS.muted,
  bgcolor: "#fff",
};

const menuItemSx = (disabled) => ({
  mx: 0.75,
  my: 0.25,
  px: 1.25,
  py: 0.75,
  minHeight: 36,
  borderRadius: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.5,
  fontSize: "0.8125rem",
  fontWeight: 500,
  lineHeight: 1.35,
  color: disabled ? ABO_COLORS.muted : ABO_COLORS.title,
  opacity: 1,
  "&.Mui-selected": {
    bgcolor: ABO_COLORS.infoBg,
    color: ABO_COLORS.infoText,
    fontWeight: 600,
    "&:hover": { bgcolor: ABO_COLORS.infoBg },
  },
  "&.Mui-disabled": {
    opacity: 0.55,
  },
});

const hintSx = {
  fontSize: "0.6875rem",
  fontWeight: 500,
  color: ABO_COLORS.muted,
  lineHeight: 1.3,
  whiteSpace: "nowrap",
};

const BUDGET_TYPE_MENU_PROPS = {
  PaperProps: {
    sx: {
      mt: 0.5,
      minWidth: 280,
      borderRadius: 2,
      border: `1px solid ${ABO_COLORS.border}`,
      boxShadow: "0 10px 28px rgba(15, 23, 42, 0.12)",
      overflow: "hidden",
    },
  },
  MenuListProps: {
    disablePadding: true,
    sx: { py: 0.75 },
  },
};

function CampaignAboBudgetTypeSelect({
  value,
  onChange,
  budgetFamily = BUDGET_FAMILY.SET,
  isLastSlot = true,
  showAllocateGroup = true,
  error = false,
  disabled = false,
  size = "small",
  sx,
}) {
  const setEnabled = budgetFamily === BUDGET_FAMILY.SET;
  const allocateEnabled = budgetFamily === BUDGET_FAMILY.ALLOCATE;
  const selectedLabel = getBudgetTypeLabel(value) || "Select type";

  return (
    <Select
      size={size}
      value={value || ""}
      error={error}
      disabled={disabled}
      onChange={onChange}
      displayEmpty
      renderValue={() => selectedLabel}
      sx={{
        ...tableControlSx,
        minWidth: 176,
        ...sx,
      }}
      MenuProps={BUDGET_TYPE_MENU_PROPS}
    >
      {showAllocateGroup ? (
        <ListSubheader disableSticky sx={groupHeaderSx}>
          Set
          {!setEnabled ? (
            <Box component="span" sx={{ ...hintSx, ml: "auto", letterSpacing: 0, fontWeight: 500 }}>
              Rule uses Allocate types
            </Box>
          ) : null}
        </ListSubheader>
      ) : null}
      {SET_BUDGET_TYPE_OPTIONS.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          disabled={!setEnabled}
          sx={menuItemSx(!setEnabled)}
        >
          {option.label}
        </MenuItem>
      ))}
      {showAllocateGroup ? <Divider sx={{ my: 0.75, borderColor: ABO_COLORS.border }} /> : null}
      {showAllocateGroup ? (
        <ListSubheader disableSticky sx={groupHeaderSx}>
          Allocate
          {!allocateEnabled ? (
            <Box component="span" sx={{ ...hintSx, ml: "auto", letterSpacing: 0, fontWeight: 500 }}>
              Rule uses Set types
            </Box>
          ) : null}
        </ListSubheader>
      ) : null}
      {showAllocateGroup
        ? ALLOCATE_BUDGET_TYPE_OPTIONS.map((option) => {
            const itemDisabled = !allocateEnabled || (option.lastSlotOnly && !isLastSlot);
            return (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={itemDisabled}
                sx={menuItemSx(itemDisabled)}
              >
                <Typography component="span" sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
                  {option.label}
                </Typography>
                {option.hint ? (
                  <Typography component="span" sx={hintSx}>
                    {option.hint}
                  </Typography>
                ) : null}
              </MenuItem>
            );
          })
        : null}
    </Select>
  );
}

export default memo(CampaignAboBudgetTypeSelect);
